import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function AlmadaaDigital() {
  const services = [
    {
      icon: 'ri-share-fill',
      title: 'Social Media Management',
      description: 'Strategic content creation, community engagement, and brand building across all major social platforms to grow your online presence and connect with your audience.'
    },
    {
      icon: 'ri-line-chart-fill',
      title: 'Digital Marketing',
      description: 'Comprehensive digital marketing strategies including SEO, SEM, email marketing, and paid advertising campaigns that drive traffic, leads, and conversions.'
    },
    {
      icon: 'ri-camera-fill',
      title: 'Photography and Videography',
      description: 'Professional photography and videography services that capture your brand story with stunning visuals, from product shoots to corporate events and promotional content.'
    },
    {
      icon: 'ri-code-s-slash-fill',
      title: 'Web and App Development',
      description: 'Custom website and mobile app development with modern design, seamless functionality, and user-friendly interfaces that deliver exceptional digital experiences.'
    },
    {
      icon: 'ri-movie-2-fill',
      title: 'Reels and Creative Content Production',
      description: 'Engaging short-form video content, reels, and creative productions optimized for social media platforms to maximize reach and audience engagement.'
    },
    {
      icon: 'ri-palette-fill',
      title: 'Brand Strategy & Design',
      description: 'Complete brand identity development including logo design, brand guidelines, and visual assets that create a memorable and cohesive brand presence.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Almadaa Digital Marketing Management
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
              A Dubai Mainland registered digital agency delivering modern marketing and creative solutions
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">About Almadaa Digital</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                <strong className="text-blue-700">Almadaa Digital Marketing Management</strong> is a fast-growing digital agency based in Dubai, UAE. As a Dubai Mainland registered company, we specialize in delivering modern marketing and creative solutions that help businesses thrive in the digital age.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our team of creative professionals and marketing experts combines strategic thinking with innovative execution to deliver campaigns that resonate with your target audience and drive measurable results.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                From startups to established enterprises, we partner with businesses across industries to elevate their digital presence, engage their customers, and achieve their marketing objectives through data-driven strategies and creative excellence.
              </p>
            </div>
            <div>
              <img 
                src="https://public.readdy.ai/ai/img_res/8320f49c0181e7a59f425c73a07fc137.jpg"
                alt="Almadaa Digital Team"
                className="w-full h-full object-cover object-top rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive digital solutions tailored to your business needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mb-6 group-hover:bg-blue-700 transition-colors">
                  <i className={`${service.icon} text-3xl text-blue-700 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-700 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Objective Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-r from-blue-700 to-blue-900 rounded-3xl p-12 text-white text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
              <i className="ri-target-fill text-4xl"></i>
            </div>
            <h2 className="text-4xl font-bold mb-6">Our Objective</h2>
            <p className="text-xl leading-relaxed max-w-4xl mx-auto">
              Empowering brands with <strong>creative excellence</strong>, <strong>strategic thinking</strong>, and <strong>measurable results</strong>. We are committed to helping businesses achieve their digital marketing goals through innovative solutions, data-driven insights, and exceptional execution that drives growth and success.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Why Choose Almadaa Digital</h2>
            <p className="text-xl text-gray-600">Your trusted partner for digital success in Dubai</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-yellow-500 rounded-full mx-auto mb-6">
                <i className="ri-user-star-fill text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Personalized Strategies</h3>
              <p className="text-gray-700">
                Tailored solutions designed specifically for your business goals and target audience
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-blue-700 rounded-full mx-auto mb-6">
                <i className="ri-team-fill text-4xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Execution</h3>
              <p className="text-gray-700">
                Skilled professionals with years of experience in digital marketing and creative services
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-yellow-500 rounded-full mx-auto mb-6">
                <i className="ri-trophy-fill text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Proven Track Record</h3>
              <p className="text-gray-700">
                Successful campaigns and satisfied clients across various industries in Dubai
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-blue-700 rounded-full mx-auto mb-6">
                <i className="ri-map-pin-fill text-4xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Dubai Market Expertise</h3>
              <p className="text-gray-700">
                Deep understanding of Dubai Mainland regulations and local market dynamics
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-yellow-500 to-yellow-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">Ready to Elevate Your Digital Presence?</h2>
          <p className="text-xl text-gray-900 mb-10">
            Let's create something amazing together. Contact us today to discuss your project.
          </p>
          <Link 
            to="/contact" 
            className="inline-block px-10 py-4 bg-blue-900 hover:bg-black text-white font-bold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Get Started Now
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
